/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Autocomplete,
  IconButton
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import produce from "immer";
import { useDispatch } from "react-redux";

import {
  useLeaveBalances,
  usePostLeaveBalance,
  usePatchLeaveBalance
} from "../../../../../hooks/query/useLeaveBalances";
import { UserMode } from "../../../../../interfaces/User";
import { useAppSelector } from "../../../../../store/hooks";
import useFiscalYears from "../../../../../hooks/useFiscalYears";
import { useLeaveTypes } from "../../../../../hooks/query/useLeaveTypes";
import { notificationAdd } from "../../../../../actions/notification";
import { employeesSortedSelector } from "../../../../../reducers/employee";
import useCurrentResourceCentre from "../../../../../hooks/useCurrentResourceCentre";
import { serviceProvidersSortedSelector } from "../../../../../reducers/serviceProvider";

const cellStyle = { border: 1, borderColor: "divider" };
const headerCellStyle = { ...cellStyle, fontWeight: 600, textAlign: "center" as const };

// eslint-disable-next-line import/prefer-default-export
export const LeaveBalances = (): React.ReactNode => {
  const dispatch = useDispatch();
  const fiscalYears = useFiscalYears();
  const rc = useCurrentResourceCentre();
  const { data: leaveTypes = [] } = useLeaveTypes();
  const { mutateAsync: postLeaveBalance } = usePostLeaveBalance();
  const { mutateAsync: patchLeaveBalance } = usePatchLeaveBalance();
  const { data: leaveBalances = [], isLoading } = useLeaveBalances();

  // selectors
  const employees = useAppSelector(employeesSortedSelector);
  const serviceProviders = useAppSelector(serviceProvidersSortedSelector);

  // local state
  const [fiscalYear, setFiscalYear] = useState<string>(fiscalYears?.[0] || "");
  const [employeeType, setEmployeeType] = useState<UserMode.Employee | UserMode.ServiceProvider>(
    UserMode.Employee
  );
  const [draftBalances, setDraftBalances] = useState<Record<number, Record<number, string>>>({});
  const [editingCell, setEditingCell] = useState<{ entityId: number; leaveTypeId: number } | null>(
    null
  );

  // variables
  const currentList = employeeType === UserMode.Employee ? employees : serviceProviders;

  // helper functions
  const handleChange = (entityId: number, leaveTypeId: number, value: string) => {
    setDraftBalances((prev) =>
      produce(prev, (draft) => {
        if (!draft[entityId]) draft[entityId] = {};
        draft[entityId][leaveTypeId] = value;
      })
    );
  };

  const handleSaveCell = async (entityId: number, leaveTypeId: number) => {
    if (!rc?.id) return;
    if (!fiscalYear) return;

    const balanceStr = draftBalances[entityId]?.[leaveTypeId];
    if (balanceStr === undefined || balanceStr === "") return;

    const balanceNum = Number(balanceStr);
    if (Number.isNaN(balanceNum)) return;

    const existingRecord = leaveBalances.find(
      (b) =>
        (employeeType === UserMode.Employee
          ? Number(b.employeeId) === Number(entityId)
          : Number(b.serviceProviderId) === Number(entityId)) &&
        Number(b.leaveTypeId) === Number(leaveTypeId) &&
        b.fiscalYear === fiscalYear
    );

    try {
      if (existingRecord) {
        if (existingRecord.balance !== balanceNum) {
          await patchLeaveBalance({
            id: existingRecord.id,
            data: { balance: balanceNum }
          });
        }
      } else {
        await postLeaveBalance({
          resourceCentreId: rc.id,
          employeeId: employeeType === UserMode.Employee ? entityId : undefined,
          serviceProviderId: employeeType === UserMode.ServiceProvider ? entityId : undefined,
          leaveTypeId,
          balance: balanceNum,
          used: 0,
          pending: 0,
          fiscalYear
        });
      }
    } catch (error) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: "Failed to update leave balance",
          variant: "error"
        })
      );
    }
  };

  const isCellChanged = (entityId: number, leaveTypeId: number) => {
    const draftValue = draftBalances[entityId]?.[leaveTypeId];
    if (draftValue === undefined) return false;

    const existingRecord = leaveBalances.find(
      (b) =>
        (employeeType === UserMode.Employee
          ? Number(b.employeeId) === Number(entityId)
          : Number(b.serviceProviderId) === Number(entityId)) &&
        Number(b.leaveTypeId) === Number(leaveTypeId) &&
        b.fiscalYear === fiscalYear
    );

    const existingValue = existingRecord?.balance?.toString();

    if (!existingRecord) {
      return draftValue !== "" && draftValue !== "0";
    }

    return draftValue !== existingValue;
  };

  // use effects
  useEffect(() => {
    const newDraft: Record<number, Record<number, string>> = {};
    const filteredBalances = leaveBalances.filter(
      (b) => !fiscalYear || b.fiscalYear === fiscalYear
    );

    filteredBalances.forEach((b) => {
      const entityId = employeeType === UserMode.Employee ? b.employeeId : b.serviceProviderId;
      if (entityId) {
        if (!newDraft[entityId]) newDraft[entityId] = {};
        newDraft[entityId][b.leaveTypeId] = b.balance?.toString() || "0";
      }
    });

    setDraftBalances(newDraft);
  }, [leaveBalances, fiscalYear, employeeType]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        p: 2,
        flexDirection: "column",
        gap: 2
      }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">Manage Leave Balances</Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 200 }}
            options={[UserMode.Employee, UserMode.ServiceProvider]}
            value={employeeType}
            onChange={(_, newValue) =>
              setEmployeeType(newValue as UserMode.Employee | UserMode.ServiceProvider)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employee Type"
                variant="outlined"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
          />

          <Autocomplete
            freeSolo
            size="small"
            sx={{ minWidth: 200 }}
            options={fiscalYears}
            value={fiscalYear}
            onChange={(_, newValue) => setFiscalYear(newValue || "")}
            onInputChange={(_, newInputValue) => setFiscalYear(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Fiscal Year"
                variant="outlined"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}
          />
        </Box>
      </Box>

      <TableContainer sx={{ flexGrow: 1, overflow: "auto" }}>
        <Table
          size="small"
          sx={{ border: 1, borderColor: "divider", borderRadius: "5px", minWidth: 800 }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...headerCellStyle,
                  width: "50px",
                  position: "sticky",
                  left: 0,
                  bgcolor: "background.paper",
                  zIndex: 2
                }}
              >
                SN
              </TableCell>
              <TableCell
                sx={{
                  ...headerCellStyle,
                  width: "200px",
                  position: "sticky",
                  left: "50px",
                  bgcolor: "background.paper",
                  zIndex: 2
                }}
              >
                Name
              </TableCell>
              {leaveTypes.map((leave) => (
                <TableCell key={leave.id} sx={{ ...headerCellStyle, minWidth: "120px" }}>
                  {leave.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {currentList.map((entity, index) => {
              const name =
                employeeType === "employee"
                  ? `${entity.firstName || ""} ${entity.lastName || ""}`.trim()
                  : `${(entity as any)?.title || ""} ${entity.firstName || ""} ${
                      entity.lastName || ""
                    }`.trim();

              return (
                <TableRow key={entity.id}>
                  <TableCell
                    sx={{
                      ...cellStyle,
                      position: "sticky",
                      left: 0,
                      bgcolor: "background.paper",
                      zIndex: 1,
                      textAlign: "center"
                    }}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellStyle,
                      position: "sticky",
                      left: "50px",
                      bgcolor: "background.paper",
                      zIndex: 1
                    }}
                  >
                    <Typography variant="body2">{name}</Typography>
                  </TableCell>
                  {leaveTypes.map((leave) => {
                    const changed = isCellChanged(entity.id, leave.id);
                    const isEditing =
                      editingCell?.entityId === entity.id && editingCell?.leaveTypeId === leave.id;
                    const value = draftBalances[entity.id]?.[leave.id] ?? "";

                    return (
                      <TableCell key={leave.id} sx={{ ...cellStyle, textAlign: "center", p: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            type="number"
                            value={value || "0"}
                            disabled={!isEditing}
                            onChange={(e) => handleChange(entity.id, leave.id, e.target.value)}
                            onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
                            sx={{
                              "& .MuiInputBase-input": {
                                padding: "4px 8px",
                                fontSize: "0.875rem",
                                WebkitTextFillColor: "inherit",
                                color: "inherit"
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: isEditing ? undefined : "none"
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && changed && !isLoading) {
                                handleSaveCell(entity.id, leave.id).then(() =>
                                  setEditingCell(null)
                                );
                              } else if (e.key === "Escape") {
                                setEditingCell(null);
                              }
                            }}
                            inputRef={(input) => {
                              if (input && isEditing && document.activeElement !== input) {
                                input.focus();
                              }
                            }}
                          />
                          {isEditing ? (
                            <IconButton
                              title="Save"
                              size="small"
                              color="primary"
                              disabled={!changed || isLoading}
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: "5px"
                              }}
                              onClick={() => {
                                handleSaveCell(entity.id, leave.id).then(() =>
                                  setEditingCell(null)
                                );
                              }}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              title="Edit"
                              size="small"
                              onClick={() =>
                                setEditingCell({ entityId: entity.id, leaveTypeId: leave.id })
                              }
                            >
                              <EditIcon fontSize="small" color="action" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}

            {currentList.length === 0 && (
              <TableRow>
                <TableCell colSpan={leaveTypes.length + 2} sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
